import { trpc } from "./trpc";
import type { OperatorId } from "@brewbase/db/types";
import {
  type Capability,
  type CorrectionDomain,
  correctionScope,
  hasCapability,
} from "@brewbase/lib";

export function useOperatorById(id: OperatorId) {
  return trpc.operator.getOperatorById.useQuery(id);
}

export function useCurrentOperator() {
  return trpc.operator.getCurrentOperator.useQuery();
}

/**
 * Whether the current operator holds a capability (the `*` wildcard satisfies
 * everything). Server-side `requireCapability` is the real enforcement — this
 * only drives UI gating. Returns false until the operator query has loaded.
 */
export function useCan(cap: Capability): boolean {
  const { data } = useCurrentOperator();
  return data ? hasCapability(data.capabilities, cap) : false;
}

/**
 * The current operator's template-correction tier in a domain: `"any"` (may
 * correct any instance), `"own"` (only owned instances), or `null` (may not
 * correct). Drives the correction entry points. Server gates are the real
 * enforcement. Returns `null` until the operator query has loaded.
 */
export function useCorrectionScope(
  domain: CorrectionDomain,
): "any" | "own" | null {
  const { data } = useCurrentOperator();
  return data ? correctionScope(data.capabilities, domain) : null;
}
